import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PublicHeader from '../../components/ui/PublicHeader';
import PublicFooter from '../../components/ui/PublicFooter';
import Icon from '../../components/AppIcon';
import SuccessIcon from './components/SuccessIcon';
import TransactionDetails from './components/TransactionDetails';
import MessageSummary from './components/MessageSummary';
import DeliveryInfo from './components/DeliveryInfo';
import SecurityMessage from './components/SecurityMessage';
import { followupStatus } from '../../api/followup';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tid = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('tid');
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!tid) {
      setLoading(false);
      setError('شناسه پیگیری یافت نشد.');
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await followupStatus(tid);
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            'دریافت اطلاعات تراکنش با مشکل مواجه شد. لطفاً دوباره تلاش کنید.',
          );
          console.error('Followup status fetch failed:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [tid]);

  const handleReturnHome = () => {
    navigate('/message-sending-interface');
  };

  const transaction = data?.transaction;
  const msgReq = data?.msg_req;

  return (
    <>
      <Helmet>
        <title>پرداخت موفق - Khejalatiam</title>
        <meta
          name="description"
          content="تراکنش شما با موفقیت انجام شد و پیام شما در صف ارسال قرار گرفت"
        />
      </Helmet>
      <div
        className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50"
        dir="rtl"
      >
        <PublicHeader />

        <main className="container mx-auto px-4 py-8 max-w-4xl">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-6">
              <div className="h-12 w-12 rounded-full border-4 border-t-transparent animate-spin border-purple-500" />
              <p className="text-lg text-gray-600">
                در حال دریافت اطلاعات تراکنش...
              </p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                <Icon name="AlertCircle" size={32} color="#dc2626" />
              </div>
              <p className="text-lg text-gray-700">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all"
              >
                تلاش مجدد
              </button>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              <div className="text-center space-y-4 md:space-y-6">
                <SuccessIcon />

                <div className="space-y-2 md:space-y-3">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                    پرداخت با موفقیت انجام شد
                  </h1>
                  <p className="text-sm md:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                    تراکنش شما با موفقیت تکمیل شد و پیام انتخابی شما برای ارسال
                    آماده است. از اعتماد شما سپاسگزاریم.
                  </p>
                </div>
              </div>

              <TransactionDetails
                referenceCode={transaction?.reference_code}
                followupCode={transaction?.id}
                amount={transaction?.amount}
                dateTime={transaction?.updated_at}
              />

              <MessageSummary
                message={msgReq?.msg}
                receiverName={msgReq?.receiver_name}
                phoneNumber={msgReq?.phone_number}
                sentAt={msgReq?.sent_at}
              />

              <DeliveryInfo
                status={msgReq?.status}
                sentAt={msgReq?.sent_at}
                tid={tid}
              />

              {msgReq.status !== 'done' && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 md:p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Icon name="Info" size={20} color="#3b82f6" />
                    </div>
                    <p className="text-sm md:text-base text-blue-800 leading-relaxed">
                      در صورتی که می‌خواهید بعدا پیام را پیگیری کنید کد پیگیری
                      را نزد خود نگه داشته و از قسمت پیگیری پیام(لینک‌های مهم)
                      اقدام نمایید.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <SecurityMessage />
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4 md:space-y-6">
                <div className="space-y-2 md:space-y-3">
                  <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                    آیا می‌خواهید پیام دیگری ارسال کنید؟
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 max-w-xl mx-auto">
                    می‌توانید به صفحه اصلی بازگردید و پیام جدیدی برای فرد دیگری
                    ارسال کنید
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleReturnHome}
                  className="inline-flex items-center justify-center gap-2 mx-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all"
                >
                  بازگشت به صفحه اصلی
                  <Icon name="ArrowRight" size={18} />
                </button>
              </div>
            </div>
          )}
        </main>

        <PublicFooter />
      </div>
    </>
  );
};

export default PaymentSuccess;
